import { Button, Checkbox, notification, Segmented } from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import { CiqIconBox } from "components/svg-icons";
import { ProjectContext } from "context/ProjectProvider";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import { resetGridFilters } from "entity-app/utils/utils";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { AgGridReact } from "ag-grid-react";
import { useHistory, useLocation } from "react-router";
import { getGridStatusBudge } from "components/ciq-log-page-header/grid-status-budge";
import { useCIQLazyQuery } from "hooks/ciq-gql-hooks";
import { MaterialExtractSpecType } from "../model";
import ImportExtractionDrawing from "./extract-upload-drawing-modal";
import TrackAsSingleMaterialDrawer from "./track-as-a-single-material";
import {
  QUERY_GET_EXTRACTION_STATUS,
  QUERY_MATERIAL_EXTRACTED_COUNT
} from "../queries";

type Prop = {
  gridRef: React.RefObject<AgGridReact<any>>;
  filterChipRef: React.RefObject<FilterChipComponentRef>;
  pageData: {
    entityList: MaterialExtractSpecType[] | undefined;
    materialHeaderMap: any;
    addMaterialsInLog: (
      selectedRows: MaterialExtractSpecType[]
    ) => Promise<void>;
  };
  projectId: string;
  statsData:
    | {
        displayCount: number;
        filterName: undefined | string;
      }
    | undefined;
  isGridReady: boolean;
};

export default function MaterialHeaderItems({
  gridRef,

  pageData,
  filterChipRef,
  statsData,
  projectId,
  isGridReady
}: Prop) {
  const { state } = useLocation() as { state: any };
  const { isFeatureFlagEnabled, metadata } = useContext(ProjectContext);
  const [fileProcessId, setFileProcessId] = useState<string | null>(
    metadata?.fileProcessId
  );
  const [loading, setLoading] = useState(false);
  const [openUploadDrawingModal, setOpenUploadDrawingModal] = useState(false);
  const [openTrackAsSingleMaterial, setOpenTrackAsSingleMaterial] =
    useState(false);

  const history = useHistory();

  const isMaterialExtractionEnabled = isFeatureFlagEnabled(
    "MATERIAL_EXTRACTION"
  );

  const [getSpecificationStatus] = useCIQLazyQuery(QUERY_GET_EXTRACTION_STATUS);

  const [getExtractionCount] = useCIQLazyQuery(QUERY_MATERIAL_EXTRACTED_COUNT);

  useEffect(() => {
    if (state?.isOpenModel) {
      setOpenUploadDrawingModal(true);
    }
  }, [state?.isOpenModel]);

  const onAllMaterialClick = useCallback(() => {
    resetGridFilters({
      setListFilter: () => {},
      api: gridRef.current?.api
    });
  }, [gridRef]);

  const actionSelectedButton = useMemo(() => {
    if (!pageData.entityList) return null;
    const selectedRows = pageData.entityList.filter((x) => x.active);
    const selectedCount = selectedRows.length;
    return (
      <div
        className="bg-ciq-gray-500 bg-opacity-70 py-1 px-4 flex items-center space-x-2 rounded-md"
        key="MaterialLogPage_BulkEdit"
      >
        <div
          className="py-1 whitespace-nowrap flex space-x-1"
          key="MaterialLogPage_Selected"
        >
          <Checkbox
            checked
            disabled={selectedCount === 0}
            onChange={() => {
              gridRef.current?.api.deselectAll();
            }}
          />
          <div>{selectedCount} Selected</div>
        </div>
        <Button
          onClick={async () => {
            setLoading(true);
            await pageData.addMaterialsInLog(selectedRows);
            setLoading(false);
            gridRef.current?.api.deselectAll();
          }}
          loading={loading}
          disabled={selectedCount === 0 || loading}
        >
          {selectedCount <= 1 ? "Track" : "Track as separate Materials"}
        </Button>
        {selectedCount > 1 && (
          <Button
            onClick={async () => {
              setOpenTrackAsSingleMaterial(true);
            }}
            loading={loading}
            disabled={selectedCount === 0 || loading}
          >
            Track as a single Material
          </Button>
        )}
      </div>
    );
  }, [pageData, loading, gridRef]);

  const headerItems = useMemo(() => {
    const menus = [] as JSX.Element[];

    if (actionSelectedButton && isGridReady) menus.push(actionSelectedButton);

    if (gridRef.current)
      menus.push(
        <FilterChipComponent
          key="MaterialLogPage_FilterChip"
          gridRef={gridRef}
          ref={filterChipRef}
        />
      );

    menus.push(
      <CiqAgSearch
        key="MaterialLogPage_search"
        gridRef={gridRef}
        placeholder="Search Materials"
      />
    );

    menus.push(
      <Button
        key="MaterialLogPage_extraction"
        type="primary"
        onClick={() => {
          setOpenUploadDrawingModal(true);
        }}
        data-testid="material-header-extraction"
      >
        Extract from Drawings
      </Button>
    );

    return menus;
  }, [actionSelectedButton, filterChipRef, gridRef, isGridReady]);

  const entityName = useMemo(
    () => (
      <div className="flex items-center justify-start">
        <CiqIconBox feature="MATERIALS" size={20} padding={8} />
        <div className="flex flex-col pl-3">
          <div className="text-black text-xl font-semibold">Materials</div>
          {fileProcessId && (
            <div className="text-xs text-black text-opacity-60 font-normal">
              Extraction in process...
            </div>
          )}
        </div>
      </div>
    ),
    [fileProcessId]
  );

  const statusBudge = useMemo(
    () =>
      getGridStatusBudge(
        {
          title: "Materials",
          totalCount: pageData.entityList?.length,
          filterStats: statsData
        },
        onAllMaterialClick
      ),
    [onAllMaterialClick, pageData.entityList?.length, statsData]
  );

  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!fileProcessId) return;

    const abortController = new AbortController();

    const stopPolling = () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      abortController.abort();
      setFileProcessId(null);
    };

    const pollExtractionStatus = async () => {
      try {
        const res = await getSpecificationStatus({ id: fileProcessId });

        console.log("Polling extraction status:", res);

        if (!res.success) {
          stopPolling();
          metadata.fileProcessId = null;
          return;
        }

        const specInfo = res.data.data.specifications_by_pk;

        switch (specInfo.status_comment) {
          case "COMPLETED": {
            const { title } = specInfo;
            const totalMaterialsExtractedRes = await getExtractionCount({
              id: fileProcessId
            });
            const totalMaterialsExtracted =
              totalMaterialsExtractedRes?.data?.data?.extraction_data_aggregate
                ?.aggregate.count || 0;
            notification.success({
              message: `${totalMaterialsExtracted} material extraction items have been successfully extracted from ${title}.`,
              duration: 0
            });
            stopPolling();
            metadata.fileProcessId = null;
            break;
          }

          case "IN_PROGRESS":
            pollTimeoutRef.current = setTimeout(pollExtractionStatus, 9000);
            break;

          default:
            stopPolling();
            metadata.fileProcessId = null;
        }
      } catch (error: any) {
        if (error.name !== "AbortError") {
          console.error("Extraction status check failed:", error);
          stopPolling();
        }
        metadata.fileProcessId = null;
      }
    };

    metadata.fileProcessId = fileProcessId;
    pollExtractionStatus();

    // Cleanup on unmount or fileProcessId change
    // eslint-disable-next-line consistent-return
    return () => {
      stopPolling();
    };
  }, [fileProcessId, getExtractionCount, getSpecificationStatus, metadata]);

  return (
    <div className="w-full px-6 py-4 flex justify-between items-center">
      <div className="flex space-x-2 items-center text-black">
        <div className="text-xl font-semibold">{entityName}</div>
        <div className="flex" style={{ minWidth: 40 }}>
          {statusBudge}
        </div>
        {isMaterialExtractionEnabled && (
          <Segmented
            options={["Log", "Extracted"]}
            value="Extracted"
            onChange={(e) => {
              if (e === "Extracted")
                history.replace(`/project/${projectId}/materials/extracted`);
              if (e === "Log")
                history.replace(`/project/${projectId}/materials`);
            }}
          />
        )}
      </div>

      <div className="flex space-x-2 items-center">{headerItems}</div>
      {openUploadDrawingModal && (
        <ImportExtractionDrawing
          openDrawer={openUploadDrawingModal}
          onCancel={() => setOpenUploadDrawingModal(false)}
          onComplete={(specId: string) => {
            setOpenUploadDrawingModal(false);
            gridRef.current?.api.deselectAll();

            setFileProcessId(specId);
          }}
        />
      )}
      {openTrackAsSingleMaterial && pageData?.entityList && (
        <TrackAsSingleMaterialDrawer
          openDrawer={openTrackAsSingleMaterial}
          onCancel={() => setOpenTrackAsSingleMaterial(false)}
          onComplete={() => {
            setOpenTrackAsSingleMaterial(false);
            gridRef.current?.api.deselectAll();
          }}
          selectedRows={pageData?.entityList.filter((x) => x.active)}
          materialHeaderMap={pageData.materialHeaderMap}
        />
      )}
    </div>
  );
}
